import printy6ImageLoader from "@lib/printy6ImageLoader";
import CustomDetail from "containers/custom/detail";
import Layout from "layout/landingPage";
import { BreadcrumbJsonLd, CarouselJsonLd, NextSeo } from "next-seo";
import React from "react";
import { useSelector } from "react-redux";
import { fetchTemplates } from "services/template";
import { RootState } from "stores/store";
import { SWRConfig, unstable_serialize } from "swr";
import { initAppData } from "utils/initAppData";
import Error from "../404";

type Props = {
  type: string;
  path: string;
  slug: string;
  slugs: string[];
  query: any;
  templates: any;
};

export default function Custom({
  type,
  path,
  slug,
  slugs,
  query,
  templates,
}: Props) {
  if (!templates) {
    return <Error statusCode={404} />;
  }

  const app = useSelector((store: RootState) => store.app);
  const { collection: collections, catalog: categories } = app;

  const breadcrumbs = React.useMemo(() => {
    if (!templates) {
      return [];
    }
    if (type === "category") {
      const arr = [];
      const activeCategory = categories.find((item) => item.key === slug);

      if (activeCategory) {
        arr.push({
          slug: activeCategory.key,
          name: activeCategory.name,
          type: "CATEGORY",
          href: null,
        });
      } else {
        for (let i = 0; i < categories.length; i++) {
          const topCategory = categories[i];
          const activeCategory = topCategory.children.find(
            (item) => item.key === slug
          );
          if (activeCategory) {
            arr.push({
              slug: topCategory.key,
              name: topCategory.name,
              type: "CATEGORY",
              href: `/custom/${topCategory.key}`,
            });
            arr.push({
              slug: activeCategory.key,
              name: activeCategory.name,
              type: "CATEGORY",
              href: null,
            });
          }
        }
      }

      return arr;
    }

    if (type === "all") {
      return [
        {
          slug: "all",
          name: "All Products",
          href: null,
        },
      ];
    }

    if (type === "favorites") {
      return [
        {
          slug: "favorite",
          name: "My Favorites",
          href: null,
        },
      ];
    }

    if (type === "collection") {
      const arr = [
        {
          slug: "collections",
          name: "Collections",
          href: null,
        },
      ];

      const activeCollection = collections.find((item) => item.slug === slug);

      if (activeCollection) {
        arr.push({
          slug: activeCollection.key,
          name: activeCollection.name,
          href: null,
        });
      }

      return arr;
    }
  }, [type, slug, collections, categories]);

  const seoAttr = getSeoAttr(
    app,
    slugs,
    templates.templates,
    templates.meta_description
  );
  const breadcrumbJsonAttr = getBreadcrumbJsonAttr(templates.breadcrumbs);
  const carouselJsonAttr = getCarouselJsonAttr(templates.templates);

  return (
    <Layout>
      <SWRConfig
        value={{
          fallback: {
            [unstable_serialize(["templates", query])]: templates,
          },
        }}
      >
        <NextSeo {...seoAttr} />
        <BreadcrumbJsonLd {...breadcrumbJsonAttr} />
        <CarouselJsonLd {...carouselJsonAttr} />
        <CustomDetail
          slugs={slugs}
          query={query}
          breadcrumbs={breadcrumbs}
          path={path}
        />
      </SWRConfig>
    </Layout>
  );
}

Custom.getInitialProps = async (ctx) => {
  await initAppData(ctx);

  let { slugs, page } = ctx.query;
  let type = "all";
  let slug = "";
  let query = {};

  if (slugs.length === 2) {
    if (slugs[0] === "collections") {
      type = "collection";
      slug = slugs[1];

      query = {
        collection: slugs[1],
        page: page || 1,
      };
    }
  } else if (slugs.length === 1) {
    if (slugs[0] === "all") {
      type = "all";
      query = {
        page: page || 1,
      };
    } else if (slugs[0] === "favorites") {
      type = "favorites";
      query = {
        isFavorite: true,
        page: page || 1,
      };
    } else {
      type = "category";
      slug = slugs[0];
      query = {
        category: slugs[0],
        page: page || 1,
      };
    }
  }

  const data = await fetchTemplates(query, {
    headers:
      ctx.req && ctx.req.headers.cookie
        ? { cookie: ctx.req.headers.cookie }
        : undefined,
  });

  let path = ctx.asPath.split("?")[0];

  return {
    type,
    path,
    slug,
    slugs,
    query,
    templates: data || null,
  };
};

const getSeoAttr = (app, slugs, templates, meta_description) => {
  return React.useMemo(() => {
    const title = calcTitle(slugs, app);

    const url = `${process.env.NEXT_PUBLIC_SITE_URL}/custom/${slugs.join("/")}`;

    const images = templates.slice(0, 3).map((item) => {
      return {
        url: printy6ImageLoader({
          src: item.first_image_src,
          quality: 1000,
        }),
        width: 1000,
        height: 1000,
      };
    });

    let description = meta_description;

    if (!description) {
      let name = "products";

      let category = getCateogry(app, slugs[0]);

      if (category) {
        name = category.name.toLowerCase();
      }

      description = `Create ${name} with your design and sell everywhere, we'll fulfill your orders for you. ✅ 200+ products. ✅ We will handle printing and shipping to your customers!`;
    }

    return {
      title,
      description,
      canonical: url,
      openGraph: {
        type: "website",
        url,
        title,
        description,
        images,
      },
    };
  }, []);
};

const calcTitle = (slugs: string[], app: any): string => {
  if (slugs.length === 2) {
    if (slugs[0] === "collections") {
      const collection = app.collection.find((item) => item.slug === slugs[1]);
      if (collection) {
        return `${collection.name} | Printy6`;
      }
    }
  } else if (slugs.length === 1) {
    if (slugs[0] === "all") {
      return "Custom Products - Create, Buy & Sell (Dropship) | Printy6";
    } else if (slugs[0] === "favorites") {
      return "Custom Products - Create, Buy & Sell (Dropship) | Printy6";
    } else {
      let category = getCateogry(app, slugs[0]);
      if (category) {
        return `Custom ${category.name} - Create, Buy & Sell (Dropship) | Printy6`;
      }
    }
  }

  return "Custom Products - Create, Buy & Sell (Dropship) | Printy6";
};

const getCateogry = (app, key) => {
  let category = app.catalog.find((item) => item.key === key);
  if (!category) {
    for (let i = 0; i < app.catalog.length; i++) {
      const topCategory = app.catalog[i];
      category = topCategory.children.find((item) => item.key === key);
      if (category) {
        break;
      }
    }
  }
  return category;
};

const getBreadcrumbJsonAttr = (breadcrumbs: any[]) => {
  const arr = [
    {
      position: 1,
      name: "Printy6",
      item: "https://www.printy6.com",
    },
    {
      position: 2,
      name: "All Products",
      item: "https://www.printy6.com/custom/all",
    },
  ];

  for (let i = 0; i < breadcrumbs.length; i++) {
    const breadcrumb = breadcrumbs[i];
    let item = "https://www.printy6.com";
    if (breadcrumb.type === "CATEGORY") {
      item = `https://www.printy6.com/custom/${breadcrumb.slug}`;
    } else if (breadcrumb.type === "COLLECTION") {
      item = `https://www.printy6.com/custom/collections/${breadcrumb.slug}`;
    }
    arr.push({
      position: arr.length + 1,
      name: breadcrumb.name,
      item,
    });
  }

  return {
    itemListElements: arr,
  };
};

const getCarouselJsonAttr = (templates) => {
  return {
    ofType: "default" as const,
    data: templates.slice(0, 5).map((item) => ({
      url: `https://www.printy6.com/design/${item.slug}`,
    })),
  };
};
